import { Outlet } from 'react-router-dom'
import { Database, History, LayoutDashboard, Settings, Users } from 'lucide-react'

import { t } from '../../lib/i18n'
import { routes } from '../../lib/routes'
import { PriceLockProvider, SidebarNav, type NavSection } from '../../components'
import { useSession, useSessionState } from '../../app/session'
import { usePeople } from '../../fixtures'

/**
 * The Owner Console shell. Accent BLUE — the trader's own console, and never to
 * be mistaken for the indigo platform console looking over his shoulder. The
 * accent is re-bound by `[data-console='owner']`; every `bg-accent` inside this
 * subtree retints without a second stylesheet.
 *
 * Price Lock lives here rather than in the platform console, because it is the
 * trader's own protection from someone reading his prices over his shoulder.
 */
export function OwnerLayout() {
  const { bookUserName, bookRole, businessName } = useSession()
  const { signOut } = useSessionState()
  const people = usePeople()

  const sections: NavSection[] = [
    {
      id: 'overview',
      title: t('o.nav.overview'),
      items: [
        {
          to: routes.owner.overview(),
          label: t('o.nav.dashboard'),
          icon: <LayoutDashboard className="size-full" />,
          end: true,
        },
      ],
    },
    {
      id: 'administration',
      title: t('o.nav.administration'),
      items: [
        {
          to: routes.owner.people(),
          label: t('o.nav.people'),
          icon: <Users className="size-full" />,
          count: people.data?.length,
        },
        {
          to: routes.owner.history(),
          label: t('o.nav.history'),
          icon: <History className="size-full" />,
        },
        {
          to: routes.owner.backup(),
          label: t('o.nav.backup'),
          icon: <Database className="size-full" />,
        },
        {
          to: routes.owner.settings(),
          label: t('o.nav.settings'),
          icon: <Settings className="size-full" />,
        },
      ],
    },
  ]

  return (
    <PriceLockProvider>
      <div className="flex h-dvh overflow-hidden" data-console="owner">
        <SidebarNav
          brand={t('p.brand')}
          brandSub={businessName}
          sections={sections}
          user={{ name: bookUserName, role: t(`role.${bookRole}`) }}
          onSignOut={() => void signOut()}
        />
        <main className="no-scrollbar flex-1 overflow-y-auto px-7 pt-6 pb-12">
          <Outlet />
        </main>
      </div>
    </PriceLockProvider>
  )
}
